import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, ErrorBanner, PageHeader } from '@zarya/ui';
import { IdentityListPane } from '@app/shared/IdentityListPane';
import painel from '@app/shared/IdentityListPane/IdentityListPane.module.css';
import { useClusterReading } from '@app/reading';
import { contractsKeys, useContract, useIdentities, useIdentityContract } from './Contracts.api';
import { ContractReleasesCard } from './ContractReleasesCard';
import { ContractStateCard } from './ContractStateCard';
import { ContractTextCard } from './ContractTextCard';
import { t } from '@app/i18n';
import s from './Contracts.module.css';

/**
 * T13 — Security Contracts (FR-069, FR-070).
 *
 * "O artefato central: capabilities aprovadas para uma release específica de uma
 * identidade. O admission, o SPIRE e o OPA consultam este contrato, cada um para
 * a sua parte."
 *
 * A identidade escolhida vive na URL (`?identidade=`) — é o que deixa o card
 * Estado de T13 e o botão de T14 apontarem um para o outro. A escolha é pela
 * lista à esquerda (item 7.1 do roteiro do PO, 28/08), não mais por combobox —
 * o mesmo `IdentityListPane` que T14 usa desde 28/08.
 */
export function ContractsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const identityURL = searchParams.get('identidade');
  const [versao, setVersao] = useState<string | null>(null);

  const identidades = useIdentities();
  const lista = identidades.data ?? [];
  // PRD §4 T13: sem `?identidade=` a tela pede a escolha — não escolhe sozinha
  // (fidelidade #B12). Quem chega de T5/T14 já vem com o parâmetro.
  const selecionada = identityURL;

  const view = useIdentityContract(selecionada);
  const historica = useContract(versao);
  // Abrir T13 pede a leitura nova do PDP (decisão 2): o L8, uma por site, o mesmo do card de autorização do T15.
  // «Publicado no PDP» mostra a foto na hora e se atualiza quando a leitura termina.
  const pdp = useClusterReading({ scope: 'pdp', refresh: [contractsKeys.todas] });

  // Trocar de identidade descarta a versão antiga que estava na tela.
  useEffect(() => setVersao(null), [selecionada]);

  const choose = (id: string) => {
    const proximos = new URLSearchParams(searchParams);
    proximos.set('identidade', id);
    setSearchParams(proximos, { replace: true });
  };

  const contractShown = versao ? (historica.data ?? null) : (view.data?.in_force ?? null);

  return (
    <>
      <PageHeader
        title={t('contracts.page.title')}
        tip={t('contracts.page.tip')}
        icon="file-shield"
      />

      {identidades.isError ? (
        <ErrorBanner
          error={identidades.error}
          title={t('contracts.page.listError')}
          onRetry={() => void identidades.refetch()}
          hideLink
        />
      ) : null}

      {view.isError ? (
        <ErrorBanner
          error={view.error}
          title={t('contracts.page.contractError')}
          onRetry={() => void view.refetch()}
          hideLink
        />
      ) : null}

      {!identidades.isLoading && !lista.length ? (
        <Card title={t('contracts.page.noIdentities')}>
          <p className={s.muted}>{t('contracts.page.noIdentitiesBody')}</p>
        </Card>
      ) : null}

      {identidades.isLoading || lista.length > 0 ? (
        <div className={painel.layout}>
          <IdentityListPane
            identidades={lista}
            selecionada={selecionada}
            onEscolher={choose}
            carregando={identidades.isLoading}
          />

          <div className={painel.detalhe}>
            {!selecionada && lista.length > 0 ? (
              <Card title={t('contracts.text.current')}>
                <p className={s.muted}>{t('contracts.page.chooseIdentity')}</p>
              </Card>
            ) : null}

            {view.isLoading ? (
              <Card title={t('contracts.text.current')}>
                <p className={s.carregando}>{t('contracts.page.loading')}</p>
              </Card>
            ) : null}

            {view.data ? (
              <div className={s.grade}>
                <div className={s.coluna}>
                  <ContractTextCard
                    view={view.data}
                    contrato={contractShown}
                    historica={Boolean(versao)}
                    onVerVigente={() => setVersao(null)}
                  />
                </div>
                <div className={s.coluna}>
                  <ContractStateCard view={view.data} readingPending={pdp.pending} />
                  <ContractReleasesCard
                    identityId={view.data.identity.id}
                    selecionado={versao}
                    onSelecionar={setVersao}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
